import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import s from './Table.module.scss';
import classNames from 'classnames';
import ArrowDown from '@/shared/assets/icons/arrow_down.svg';
import { memo, MutableRefObject, useEffect, useImperativeHandle, useMemo } from 'react';

interface TableProps<T extends { id: number | string }> {
  data: T[];
  columns: ColumnDef<T>[];
  onSelect: (data: T[]) => void;
  tableRef: MutableRefObject<any>;
}

const Table = <T extends { id: number | string }>(props: TableProps<T>) => {
  const { data, columns, onSelect, tableRef } = props;

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const selectedArray = Object.keys(table.getState().rowSelection);

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedRowData = useMemo(() => selectedRows.map((row) => row.original), [selectedRows]);

  useEffect(() => {
    const currentIds = new Set(data.map((row) => String(row.id)));
    table.setRowSelection((selection) => {
      const entries = Object.entries(selection).filter(([id]) => currentIds.has(id));
      return entries.length === Object.keys(selection).length ? selection : Object.fromEntries(entries);
    });
  }, [data, table]);

  useEffect(() => {
    onSelect(selectedRowData);
  }, [onSelect, selectedRowData]);
  useImperativeHandle(tableRef, () => ({
    resetSelectedRows: () => table.toggleAllRowsSelected(false),
  }));
  const getLifecycleClass = (row: { original: T }) => {
    const status = (row.original as { accountStatus?: string | null }).accountStatus;
    if (status === 'deleted') return s.deleted;
    return undefined;
  };

  const getLifecycleBackground = (row: { original: T }) => {
    const status = (row.original as { accountStatus?: string | null }).accountStatus;
    if (status === 'deleted') return 'var(--red-light-100)';
    return undefined;
  };

  return (
    <table className={s.table}>
      <thead className={s.thead}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className={s.tr}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={classNames(s.th, (header.column.columnDef as any)?.meta?.thClassName)}
                style={(header.column.columnDef as any)?.meta?.thStyle}
              >
                {header.isPlaceholder ? null : (
                  <div
                    {...{
                      className: header.column.getCanSort() ? s.sortHeader : '',
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: <ArrowDown className={s.icon} />,
                      desc: <ArrowDown className={s.icon + ' ' + s.revert} />,
                    }[header.column.getIsSorted() as string] ?? null}
                  </div>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className={s.tbody}>
        {table.getRowModel().rows.map((row) => (
          <tr
            key={row.id}
            data-lifecycle={(row.original as { accountStatus?: string | null }).accountStatus ?? 'active'}
            className={classNames(s.tr, getLifecycleClass(row), {
              [s.active]: selectedArray.includes(row.id),
            })}
          >
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className={classNames(s.td, (cell.column.columnDef as any)?.meta?.tdClassName)}
                style={{
                  ...(cell.column.columnDef as any)?.meta?.tdStyle,
                  ...(getLifecycleBackground(row) ? { backgroundColor: getLifecycleBackground(row) } : {}),
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default memo(Table) as typeof Table;
